/**
 * Exports a lexicon to the same CSV shape the importer accepts (VE-24948 / U5).
 *
 * ROUND-TRIP IS THE REQUIREMENT, not "some CSV". The columns are
 * `LEXICON_CSV_COLUMNS` in their declared order and the `type` cell carries one
 * of the two canonical words, `keyword` or `regex` — see `csvTypeOf`. Both are
 * in `csvValidator`'s accepted vocabulary, so what this writes is a file this
 * app can read back without translation. That now includes the redaction code:
 * the file names it as well as identifying it, so an exported lexicon is
 * editable by someone who has never seen a code id.
 *
 * TOMBSTONES ARE NOT EXPORTED. A deleted entry does not match anything, so
 * putting it in the file would re-introduce it on the next import — turning an
 * export/import round trip into a way to undo a deletion nobody asked to undo.
 *
 * FORMULA INJECTION IS IN SCOPE HERE, unlike in most of this module. Terms are
 * user-authored and land in a file a reviewer opens in Excel or Sheets, so a
 * term beginning `=`, `+`, `-`, `@`, tab or CR is a live formula, which
 * `Papa.unparse` does nothing about — it quotes separators, it does not
 * neutralise formulas. `guardCsvCell` handles it, and lives in
 * `csvFormulaGuard` beside the inverse the importer applies: the guard is part
 * of the round trip, and keeping the two ends apart is what let an exported
 * `-1234` come back as `'-1234` and match nothing (review nit N1).
 */

import Papa from 'papaparse';

import { guardCsvCell } from './csvFormulaGuard';

import {
  LEXICON_CSV_COLUMNS,
  Lexicon,
  LexiconCsvRedactionCode,
  LexiconEntry,
  liveEntries,
} from './models';

/**
 * The `type` cell: the two CANONICAL words, `keyword` and `regex`.
 *
 * Not `lexiconEntryKind`, which answers `pattern` — the UI's soft word for the
 * kind. In a file the author edits by hand the useful word is the one that says
 * what the cell beside it must contain, which is why the composer's own
 * placeholder for that kind reads "Type Regex". The importer still accepts
 * `pattern`, along with `phrase` and `term`, so a file written to the old
 * vocabulary imports unchanged — but only these two are documented, and only
 * these two are written.
 */
function csvTypeOf(entry: LexiconEntry): string {
  return entry.type === 'regex' ? 'regex' : 'keyword';
}

/**
 * One row per live entry, in `LEXICON_CSV_COLUMNS` order.
 *
 * ALL THREE CODE COLUMNS, and the id is no longer the only useful one. The
 * DISPLAY CODE comes first because it is what the importer reads by default and
 * what an author writing a file from scratch would type; the name follows for
 * whoever prefers it; the id is still written and still wins on re-import.
 *
 * A code the lookup does not know leaves the two human columns BLANK while the
 * id stays. That is the soft-deleted case, and blank is the honest answer: the
 * name is not resolvable any more, and re-importing the row still stamps the
 * same code because the id carried it.
 */
function rowOf(
  entry: LexiconEntry,
  codesById: ReadonlyMap<string, LexiconCsvRedactionCode>
): string[] {
  const code = entry.redactionCodeId
    ? codesById.get(entry.redactionCodeId)
    : undefined;
  return [
    entry.term,
    csvTypeOf(entry),
    entry.caseSensitive ? 'true' : '',
    entry.wholeWord ? 'true' : '',
    code?.code ?? '',
    code?.codeName ?? '',
    entry.redactionCodeId ?? '',
  ].map(guardCsvCell);
}

/**
 * The CSV text for a lexicon's live entries.
 *
 * `redactionCodes` is the org's LIVE codes, used only to name the ids the
 * entries already carry. It defaults to empty so a caller that has none — or
 * cannot get them — still produces a file whose id column round-trips; the
 * name columns are simply blank.
 */
export function lexiconToCsv(
  lexicon: Lexicon,
  redactionCodes: readonly LexiconCsvRedactionCode[] = []
): string {
  const codesById = new Map(redactionCodes.map((c) => [c.id, c]));
  const rows = liveEntries(lexicon.entries).map((e) => rowOf(e, codesById));
  return Papa.unparse([[...LEXICON_CSV_COLUMNS], ...rows]);
}

/**
 * A filename derived from the lexicon's name: `<name>.lexicon.csv`.
 *
 * The `.lexicon` infix is what tells someone six months later that a file in
 * their downloads folder is a lexicon rather than any of the other CSVs this
 * app produces — the redaction report and the audit log among them — and it
 * survives the rename people give a file before mailing it on.
 *
 * `downloadBlob` sanitises path characters itself; this only collapses the
 * whitespace and punctuation that make a name awkward to type at a shell, and
 * keeps a fallback so an all-punctuation name still produces a usable file.
 */
export function lexiconCsvFilename(lexicon: Lexicon): string {
  const stem =
    lexicon.name
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase() || 'lexicon';
  return `${stem}.lexicon.csv`;
}
